/**
 * Enhanced data service for complete session and analysis management
 * Supports saving and retrieving all application data including sessions, analysis, and openers
 */
import { supabase } from '../lib/supabase.js';

// ============ SESSION MANAGEMENT ============

/**
 * Create or update an analysis session
 * @param {Object} sessionData - Session information
 * @returns {Promise<Object>} - Created/updated session
 */
export const saveAnalysisSession = async (sessionData) => {
  try {
    const { data, error } = await supabase
      .from('analysis_sessions')
      .upsert({
        session_id: sessionData.sessionId,
        session_name: sessionData.sessionName,
        session_type: sessionData.sessionType || 'email_verification',
        total_leads: sessionData.totalLeads || 0,
        verified_leads: sessionData.verifiedLeads || 0,
        safe_leads: sessionData.safeLeads || 0,
        status: (sessionData.status || 'active').substring(0, 50), // Truncate to 50 chars to match DB constraint
        metadata: sessionData.metadata || {}
      })
      .select()
      .single();

    if (error) {
      // Check if it's a table not found error
      if (error.message && (error.message.includes('relation') || error.message.includes('table'))) {
        console.warn('analysis_sessions table does not exist, skipping database save');
        return null; // Return null instead of throwing
      }
      throw error;
    }
    return data;
  } catch (error) {
    console.error('Error saving analysis session:', error);
    // Don't throw error for table not found, allow app to continue with localStorage
    if (error.message && (error.message.includes('fetch failed') || error.message.includes('relation') || error.message.includes('table'))) {
      console.warn('Database unavailable, continuing with localStorage only');
      return null;
    }
    throw error;
  }
};

/**
 * Get all analysis sessions with summary data
 * @returns {Promise<Array>} - Array of sessions with summary
 */
export const getAllSessions = async () => {
  try {
    const { data, error } = await supabase
      .from('analysis_sessions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      // Check if it's a table not found error
      if (error.message && (error.message.includes('relation') || error.message.includes('table'))) {
        console.warn('analysis_sessions table does not exist, returning empty array');
        return [];
      }
      throw error;
    }
    return data || [];
  } catch (error) {
    console.error('Error fetching sessions:', error);
    // Don't throw error for table not found, allow app to continue with localStorage
    if (error.message && (error.message.includes('fetch failed') || error.message.includes('relation') || error.message.includes('table'))) {
      console.warn('Database unavailable, returning empty sessions array');
      return [];
    }
    throw error;
  }
};

/**
 * Get a specific session with all related data
 * @param {string} sessionId - Session ID
 * @returns {Promise<Object>} - Complete session data
 */
export const getSessionWithData = async (sessionId) => {
  try {
    let session = null;
    
    // Try to get session info
    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('analysis_sessions')
        .select('*')
        .eq('session_id', sessionId);

      if (sessionError) {
        if (sessionError.message && (sessionError.message.includes('relation') || sessionError.message.includes('table'))) {
          console.warn('analysis_sessions table does not exist, using default session');
          session = {
            session_id: sessionId,
            session_name: 'Default Session',
            session_type: 'email_verification',
            total_leads: 0,
            verified_leads: 0,
            safe_leads: 0,
            status: 'active',
            metadata: {},
            created_at: new Date().toISOString()
          };
        } else {
          throw sessionError;
        }
      } else if (sessionData && sessionData.length > 0) {
        session = sessionData[0]; // Take the first session if multiple exist
      } else {
        // No session found, create default
        console.warn('No session found for ID:', sessionId, 'using default session');
        session = {
          session_id: sessionId,
          session_name: 'Default Session',
          session_type: 'email_verification',
          total_leads: 0,
          verified_leads: 0,
          safe_leads: 0,
          status: 'active',
          metadata: {},
          created_at: new Date().toISOString()
        };
      }
    } catch (fetchError) {
      if (fetchError.message && fetchError.message.includes('fetch failed')) {
        console.warn('Database unavailable, using default session');
        session = {
          session_id: sessionId,
          session_name: 'Default Session',
          session_type: 'email_verification',
          total_leads: 0,
          verified_leads: 0,
          safe_leads: 0,
          status: 'active',
          metadata: {},
          created_at: new Date().toISOString()
        };
      } else {
        throw fetchError;
      }
    }

    // Get verified leads
    const { data: leads, error: leadsError } = await supabase
      .from('verified_leads')
      .select('*')
      .eq('session_id', sessionId);

    if (leadsError && !leadsError.message.includes('fetch failed')) throw leadsError;

    // Get website analysis (may not exist)
    let websites = [];
    try {
      const { data: websiteData, error: websitesError } = await supabase
        .from('website_analysis')
        .select('*')
        .eq('session_id', sessionId);
      
      if (!websitesError) {
        websites = websiteData || [];
      }
    } catch (websiteError) {
      console.warn('Could not fetch website analysis:', websiteError.message);
    }

    // Get generated openers (may not exist)
    let openers = [];
    try {
      const { data: openerData, error: openersError } = await supabase
        .from('generated_openers')
        .select('*')
        .eq('session_id', sessionId);
      
      if (!openersError) {
        openers = openerData || [];
      }
    } catch (openerError) {
      console.warn('Could not fetch generated openers:', openerError.message);
    }

    return {
      session,
      leads: leads || [],
      websites,
      openers
    };
  } catch (error) {
    console.error('Error fetching session data:', error);
    // Return minimal session data if everything fails
    return {
      session: {
        session_id: sessionId,
        session_name: 'Default Session',
        session_type: 'email_verification',
        total_leads: 0,
        verified_leads: 0,
        safe_leads: 0,
        status: 'active',
        metadata: {},
        created_at: new Date().toISOString()
      },
      leads: [],
      websites: [],
      openers: []
    };
  }
};

// ============ WEBSITE ANALYSIS MANAGEMENT ============

/**
 * Save website analysis data
 * @param {Object} analysisData - Website analysis information
 * @returns {Promise<Object>} - Saved analysis data
 */
export const saveWebsiteAnalysis = async (analysisData) => {
  try {
    const { data, error } = await supabase
      .from('website_analysis')
      .insert({
        session_id: analysisData.sessionId,
        website_url: analysisData.websiteUrl,
        company_name: analysisData.companyName,
        analysis_data: analysisData.analysisData,
        pain_points: analysisData.painPoints || [],
        value_propositions: analysisData.valuePropositions || [],
        key_features: analysisData.keyFeatures || [],
        target_audience: analysisData.targetAudience,
        industry: analysisData.industry,
        analysis_status: (analysisData.status || 'completed').substring(0, 50) // Truncate to 50 chars to match DB constraint
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving website analysis:', error);
    throw error;
  }
};

/**
 * Get website analysis for a session
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} - Array of website analyses
 */
export const getWebsiteAnalysis = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('website_analysis')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching website analysis:', error);
    throw error;
  }
};

// ============ OPENER MANAGEMENT ============

/**
 * Save generated opener
 * @param {Object} openerData - Opener information
 * @returns {Promise<Object>} - Saved opener data
 */
export const saveGeneratedOpener = async (openerData) => {
  try {
    const { data, error } = await supabase
      .from('generated_openers')
      .insert({
        session_id: openerData.sessionId,
        lead_id: openerData.leadId,
        website_analysis_id: openerData.websiteAnalysisId,
        opener_type: openerData.openerType,
        opener_content: openerData.openerContent,
        opener_style: openerData.openerStyle,
        personalization_data: openerData.personalizationData || {},
        performance_metrics: openerData.performanceMetrics || {},
        is_favorite: openerData.isFavorite || false
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving opener:', error);
    throw error;
  }
};

/**
 * Get all openers for a session
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} - Array of openers with lead data
 */
export const getSessionOpeners = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('generated_openers')
      .select(`
        *,
        verified_leads:lead_id(*),
        website_analysis:website_analysis_id(*)
      `)
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching session openers:', error);
    throw error;
  }
};

/**
 * Update opener performance metrics
 * @param {number} openerId - Opener ID
 * @param {Object} metrics - Performance metrics
 * @returns {Promise<Object>} - Updated opener
 */
export const updateOpenerMetrics = async (openerId, metrics) => {
  try {
    const { data, error } = await supabase
      .from('generated_openers')
      .update({ performance_metrics: metrics })
      .eq('id', openerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating opener metrics:', error);
    throw error;
  }
};

// ============ CSV UPLOAD TRACKING ============

/**
 * Track CSV upload
 * @param {Object} uploadData - Upload information
 * @returns {Promise<Object>} - Upload record
 */
export const trackCsvUpload = async (uploadData) => {
  try {
    const { data, error } = await supabase
      .from('csv_uploads')
      .insert({
        session_id: uploadData.sessionId,
        filename: uploadData.filename,
        total_rows: uploadData.totalRows,
        processed_rows: uploadData.processedRows,
        upload_status: uploadData.status || 'processing',
        file_metadata: uploadData.metadata || {}
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error tracking CSV upload:', error);
    throw error;
  }
};

// ============ EXPORT TRACKING ============

/**
 * Track data export
 * @param {Object} exportData - Export information
 * @returns {Promise<Object>} - Export record
 */
export const trackDataExport = async (exportData) => {
  try {
    const { data, error } = await supabase
      .from('export_history')
      .insert({
        session_id: exportData.sessionId,
        export_type: exportData.exportType,
        export_format: exportData.exportFormat,
        filename: exportData.filename,
        record_count: exportData.recordCount,
        export_data: exportData.exportData || {}
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error tracking export:', error);
    throw error;
  }
};

// ============ UTILITY FUNCTIONS ============

/**
 * Delete a session and all related data
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} - Success status
 */
export const deleteSessionData = async (sessionId) => {
  try {
    // Delete in reverse order of dependencies
    await supabase.from('generated_openers').delete().eq('session_id', sessionId);
    await supabase.from('website_analysis').delete().eq('session_id', sessionId);
    await supabase.from('verified_leads').delete().eq('session_id', sessionId);
    await supabase.from('csv_uploads').delete().eq('session_id', sessionId);
    await supabase.from('export_history').delete().eq('session_id', sessionId);
    await supabase.from('analysis_sessions').delete().eq('session_id', sessionId);

    return true;
  } catch (error) {
    console.error('Error deleting session data:', error);
    throw error;
  }
};

/**
 * Get session statistics
 * @param {string} sessionId - Session ID
 * @returns {Promise<Object>} - Session statistics
 */
export const getSessionStats = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('session_summary')
      .select('*')
      .eq('session_id', sessionId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching session stats:', error);
    throw error;
  }
};